'use client';

import type { KeyboardShortcutId } from '@/lib/keyboard-shortcuts/registry';
import {
  Check,
  CheckCircle2,
  CircleDashed,
  GitMerge,
  GitPullRequest,
  GitPullRequestClosed,
  Loader2,
  X,
} from 'lucide-react';
import { isValidElement, memo } from 'react';
import type { ComponentProps, ReactNode } from 'react';

import type { Command, CommandPullRequest } from './types';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { CommandGroup, CommandItem } from '@/components/ui/command';
import { KeyboardKey } from '@/components/ui/keyboard-key';
import { KEYBOARD_SHORTCUTS } from '@/lib/keyboard-shortcuts/registry';
import { altChar, cn, metaChar } from '@/lib/utils';
import { getShortRelativeTime } from '@/lib/utils/date-formatters';

/** A palette section: rows inset from the panel edge, its heading aligned with their icons. */
export function PaletteGroup({ className, ...props }: ComponentProps<typeof CommandGroup>) {
  return (
    <CommandGroup
      className={cn('px-1.5 py-1 [&_[cmdk-group-heading]]:px-2.5', className)}
      {...props}
    />
  );
}

/** A 36px palette row. */
export function PaletteRow({ className, ...props }: ComponentProps<typeof CommandItem>) {
  return (
    <CommandItem className={cn('group h-9 gap-2.5 rounded-md px-2.5 py-0', className)} {...props} />
  );
}

const SHORTCUT_KEYS = new Map(KEYBOARD_SHORTCUTS.map((entry) => [entry.id, entry.keys[0] ?? []]));

function getDisplayKey(key: string) {
  switch (key) {
    case 'Mod':
      return metaChar();
    case 'Alt':
      return altChar();
    case 'Shift':
      return '⇧';
    case 'Enter':
      return '↵';
    default:
      return key;
  }
}

/** Key caps, quiet enough to sit at the end of a row or in the footer. */
export function PaletteKeys({ keys }: { keys: readonly string[] }) {
  return (
    <span className="flex shrink-0 items-center gap-1">
      {keys.map((key, index) => (
        <KeyboardKey
          // eslint-disable-next-line react/no-array-index-key -- a key can repeat within a sequence, as in Esc Esc
          key={`${key}-${index}`}
          className="h-[18px] min-w-[18px] rounded-[4px] bg-transparent px-1 text-[11px] font-normal shadow-none"
        >
          {key}
        </KeyboardKey>
      ))}
    </span>
  );
}

/** The first key sequence of a `KEYBOARD_SHORTCUTS` entry, as key caps. */
export function PaletteShortcut({ shortcutId }: { shortcutId: KeyboardShortcutId }) {
  const keys = SHORTCUT_KEYS.get(shortcutId);
  return keys && keys.length > 0 ? <PaletteKeys keys={keys.map(getDisplayKey)} /> : null;
}

/** A command label that leads with where the command goes, muted: "AI review › Ignore files". */
export function BreadcrumbLabel({ location, title }: { location: string; title: string }) {
  return (
    <span className="flex min-w-0 items-center">
      <span className="shrink-0 text-zinc-500 group-data-[selected=true]:text-zinc-300">
        {location}
      </span>
      <span
        aria-hidden="true"
        className="mx-1 shrink-0 text-sm leading-none text-zinc-500 group-data-[selected=true]:text-zinc-300"
      >
        ›
      </span>
      <span className="min-w-0 truncate text-zinc-100 group-data-[selected=true]:text-white">
        {title}
      </span>
    </span>
  );
}

export function CommandItemIcon({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center text-muted-foreground group-data-[selected=true]:text-accent-foreground">
      <div
        className={cn(
          'flex items-center justify-center size-4',
          '[&>svg]:size-4! [&>svg]:w-4! [&>svg]:h-4! [&>svg]:min-w-4! [&>svg]:min-h-4! [&>svg]:max-w-4! [&>svg]:max-h-4!',
        )}
      >
        {children}
      </div>
    </div>
  );
}

/** A progress line inside the list. It is not an option, so the arrow keys skip it. */
export function PaletteStatusRow({ children }: { children: string }) {
  return (
    <div
      aria-live="polite"
      className="flex h-9 w-full items-center gap-2.5 px-2.5 text-sm text-muted-foreground"
      role="status"
    >
      <CommandItemIcon>
        <Loader2 aria-hidden className="motion-safe:animate-spin" />
      </CommandItemIcon>
      <span className="min-w-0 truncate">{children}</span>
    </div>
  );
}

function renderCommandLabel(command: Command, breadcrumb: string | undefined): ReactNode {
  const label = command.displayLabel ?? command.label;

  if (breadcrumb) {
    return (
      <span className="block truncate">
        <span className="text-muted-foreground">{`${breadcrumb} › `}</span>
        {label}
      </span>
    );
  }

  if (isValidElement(label)) {
    return label;
  }

  return <span className="block truncate">{label}</span>;
}

function PullRequestStateIcon({ pullRequest }: { pullRequest: CommandPullRequest }) {
  if (pullRequest.state === 'merged') {
    return <GitMerge className="text-purple-500/80" />;
  }
  if (pullRequest.state === 'draft') {
    return <CircleDashed className="text-muted-foreground" />;
  }
  if (pullRequest.state === 'closed') {
    return <GitPullRequestClosed className="text-muted-foreground" />;
  }
  if (pullRequest.reviewState === 'approved') {
    return <CheckCircle2 className="text-green-500/80" />;
  }
  if (pullRequest.reviewState === 'changes-requested') {
    return <X className="text-red-500/80" />;
  }
  if (pullRequest.reviewState === 'review-required') {
    return <span className="inline-block size-2.5 rounded-full bg-yellow-700" />;
  }
  return <GitPullRequest className="text-muted-foreground" />;
}

// What the state icon shows, for screen readers.
function getPullRequestStateLabel(pullRequest: CommandPullRequest) {
  if (pullRequest.state !== 'open') {
    return pullRequest.state.charAt(0).toUpperCase() + pullRequest.state.slice(1);
  }
  if (pullRequest.reviewState === 'approved') {
    return 'Approved';
  }
  if (pullRequest.reviewState === 'changes-requested') {
    return 'Changes requested';
  }
  if (pullRequest.reviewState === 'review-required') {
    return 'Review required';
  }
  return 'Open';
}

function PullRequestRow({ pullRequest }: { pullRequest: CommandPullRequest }) {
  const { authorLogin, authorAvatarUrl, updatedAt } = pullRequest;

  return (
    <>
      <CommandItemIcon>
        <PullRequestStateIcon pullRequest={pullRequest} />
      </CommandItemIcon>
      <span className="sr-only">{getPullRequestStateLabel(pullRequest)}</span>
      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        <span className="truncate">{pullRequest.title}</span>
        {pullRequest.needsReview && (
          <>
            <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-amber-400" />
            <span className="sr-only">Needs you</span>
          </>
        )}
      </span>
      <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
        <span className="tabular-nums">{`${pullRequest.repo} #${pullRequest.number}`}</span>
        {authorLogin && (
          <Avatar className="size-4">
            <AvatarImage
              src={authorAvatarUrl ?? undefined}
              alt={`@${authorLogin}`}
              loading="lazy"
              priority={false}
              width={32}
              height={32}
            />
            <AvatarFallback className="text-[9px]">
              {authorLogin.slice(0, 1).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        )}
        {updatedAt && (
          <span className="min-w-6 text-right tabular-nums">{getShortRelativeTime(updatedAt)}</span>
        )}
      </span>
    </>
  );
}

/** The pull request the palette's commands act on, shown above the input. */
export function PaletteTargetChip({ target }: { target: CommandPullRequest }) {
  return (
    <div className="flex px-4 pt-3">
      <div className="flex h-6 min-w-0 items-center gap-1.5 rounded-md border px-2 text-xs text-muted-foreground [&_svg]:size-3.5 [&_svg]:shrink-0">
        <PullRequestStateIcon pullRequest={target} />
        <span className="shrink-0 tabular-nums">{`${target.repo} #${target.number}`}</span>
        <span className="truncate">{target.title}</span>
      </div>
    </div>
  );
}

type CommandItemRendererProps = {
  command: Command;
  /**
   * Whether the row is highlighted. Unused in the markup, but in controlled mode cmdk reads the
   * highlight from the rendered rows before it scrolls and sets aria-activedescendant, so a row
   * whose highlight changed has to re-render in that same pass.
   */
  selected: boolean;
  /** "Diff view › Layout" for a mode's option listed in the root search. */
  breadcrumb?: string;
  /** The row's number in a numbered mode, shown in place of its shortcut. */
  ordinal?: number;
  onSelect: (commandId: string) => void;
};

export const CommandItemRenderer = memo(
  ({ command, breadcrumb, ordinal, onSelect }: CommandItemRendererProps) => {
    return (
      <PaletteRow value={command.id} onSelect={() => onSelect(command.id)}>
        {command.pullRequest ? (
          <PullRequestRow pullRequest={command.pullRequest} />
        ) : (
          <>
            {command.icon && <CommandItemIcon>{command.icon}</CommandItemIcon>}
            <div className="min-w-0 flex-1">{renderCommandLabel(command, breadcrumb)}</div>
            {ordinal ? (
              <PaletteKeys keys={[String(ordinal)]} />
            ) : (
              command.shortcutId && <PaletteShortcut shortcutId={command.shortcutId} />
            )}
            {command.checked && (
              <>
                <Check aria-hidden className="text-muted-foreground" />
                <span className="sr-only">Checked</span>
              </>
            )}
          </>
        )}
      </PaletteRow>
    );
  },
);
